// Data hook for the Target Group observability dashboard (BOF-65).
// Overview fetches immediately. Dynamics and financial are lazy (enabled flags).
// Each effect uses an `ignore` flag to discard stale responses on period/scope
// change. A `refreshKey` bump triggers manual refetch without duplicating logic.

import { useCallback, useEffect, useState } from "react";
import {
  getOverviewAnalytics,
  getDynamicsAnalytics,
  getFinancialAnalytics,
} from "../../../../services/targetGroupAnalytics";
import type {
  AnalyticsScope,
  PeriodRange,
  OverviewAnalytics,
  DynamicsAnalytics,
  FinancialAnalytics,
  Availability,
  TargetGroupDashboardData,
} from "../../../../types/targetGroupAnalytics";

const isFinanciallyEmpty = (data: FinancialAnalytics): boolean =>
  data.moneySavedTotal.amount === 0 &&
  data.savingsByGroup.length === 0 &&
  data.savingsByMember.length === 0;

interface Options {
  dynamicsEnabled: boolean;
  financialEnabled: boolean;
}

export function useTargetGroupAnalytics(
  scope: AnalyticsScope,
  period: PeriodRange,
  { dynamicsEnabled, financialEnabled }: Options,
): TargetGroupDashboardData & { refetch: () => void } {
  const [overview, setOverview] = useState<OverviewAnalytics | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState<string | null>(null);

  const [dynamics, setDynamics] = useState<DynamicsAnalytics | null>(null);
  const [dynamicsLoading, setDynamicsLoading] = useState(false);

  const [financial, setFinancial] = useState<Availability<FinancialAnalytics>>({ state: "ok" });
  const [financialLoading, setFinancialLoading] = useState(false);

  // Bumped by refetch() to re-run all active effects without changing deps logic
  const [refreshKey, setRefreshKey] = useState(0);

  // --- Overview ---
  useEffect(() => {
    let ignore = false;
    setOverviewLoading(true);
    setOverviewError(null);

    getOverviewAnalytics(scope, period.to)
      .then((data) => { if (!ignore) { setOverview(data); setOverviewLoading(false); } })
      .catch((err) => {
        if (!ignore) {
          console.error("Target Group overview analytics failed:", err);
          setOverview(null);
          setOverviewError("Unable to load Target Group analytics. Please retry.");
          setOverviewLoading(false);
        }
      });

    return () => { ignore = true; };
  }, [scope, period, refreshKey]);

  // --- Dynamics (lazy) ---
  useEffect(() => {
    if (!dynamicsEnabled) return;
    let ignore = false;
    setDynamicsLoading(true);

    getDynamicsAnalytics(scope, period)
      .then((data) => { if (!ignore) { setDynamics(data); setDynamicsLoading(false); } })
      .catch((err) => {
        if (!ignore) {
          console.error("Target Group dynamics analytics failed:", err);
          setDynamics(null);
          setDynamicsLoading(false);
        }
      });

    return () => { ignore = true; };
  }, [scope, period, dynamicsEnabled, refreshKey]);

  // --- Financial (lazy) ---
  useEffect(() => {
    if (!financialEnabled) return;
    let ignore = false;
    setFinancialLoading(true);

    getFinancialAnalytics(scope, period.to)
      .then((data) => {
        if (!ignore) {
          setFinancial(isFinanciallyEmpty(data) ? { state: "empty", data } : { state: "ok", data });
          setFinancialLoading(false);
        }
      })
      .catch((err) => {
        if (!ignore) {
          console.error("Target Group financial analytics failed:", err);
          setFinancial({ state: "unavailable", reason: "Financial data is currently unavailable." });
          setFinancialLoading(false);
        }
      });

    return () => { ignore = true; };
  }, [scope, period, financialEnabled, refreshKey]);

  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

  return {
    overview,
    dynamics,
    financial,
    loading: overviewLoading,
    dynamicsLoading,
    financialLoading,
    error: overviewError,
    financialUnavailable: financial.state === "unavailable",
    refetch,
  };
}
